import React, { useState, useLayoutEffect, useEffect } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { MainColors } from '../../../../constants/Colors';
import FontSizes from '../../../../constants/FontSizes';
import LinkStyleButton from '../../../../components/Buttons/LinkStyleButton';
import BallotItemForm from './BallotItemForm';
import { HeaderBackButton } from '@react-navigation/stack';
import HeaderButton from '../../../../components/Buttons/HeaderButton';
import InfoMessageBox from '../../../../components/InfoMessageBox';
import { ScrollView } from 'react-native-gesture-handler';

const CreateOrEditBallotItem = ({ navigation, ballot, ballotItem, setBallot, setShowBallotItemScreen }) => {
    const [title, setTitle] = useState(ballotItem.ballotItemTitle);
    const [description, setDescription] = useState(ballotItem.ballotItemDescription);
    const [choices, setChoices] = useState(ballotItem.choices);
    const [enteredChoiceTitle, setEnteredChoiceTitle] = useState('');
    const [enteredChoiceExplanation, setEnteredChoiceExplanation] = useState('');
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [areTextInputsEditable, setAreTextInputsEditable] = useState(false);
    const [choiceDeleteButtonDisabled, setChoiceDeleteButtonDisabled] = useState(false);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: ballotItem.ballotItemId === undefined ? 'Create Ballot Item' : 'Edit Ballot Item',

            headerRight: () => (
                <HeaderButton
                    onPress={onSavePressed}
                    disabled={saveButtonDisabled}
                    accessibilityLabel="Save"
                    accessibilityHint="Press this button to save changes to this ballot item"
                    buttonText="Save"
                />
            ),
            headerLeft: () => (
                <HeaderBackButton
                    onPress={() => {
                        setShowBallotItemScreen(false)
                    }}
                    label="Back"
                    tintColor="#fff"
                />
            )
        });
    }, [navigation, saveButtonDisabled]);

    useEffect(() => {
        if (ballot.canBallotBeModified === false) {
            setAreTextInputsEditable(false);
            setSaveButtonDisabled(true);
            setChoiceDeleteButtonDisabled(true);
        } else {
            setAreTextInputsEditable(true);
            setSaveButtonDisabled(false);
            setChoiceDeleteButtonDisabled(false);

            if (!title || choices.length === 0) {
                setSaveButtonDisabled(true);
            } else {
                setSaveButtonDisabled(false);
            }
        }
    }, [title, choices, saveButtonDisabled, enteredChoiceTitle, enteredChoiceExplanation]);

    const addChoiceHandler = () => {
        if (enteredChoiceTitle) {

            setChoices([
                ...choices,
                {
                    choiceTitle: enteredChoiceTitle,
                    choiceExplanation: enteredChoiceExplanation
                }
            ]);

            setEnteredChoiceTitle('');
            setEnteredChoiceExplanation('');
        }
    }

    const deleteChoicePressedHandler = (choice) => {
        const itemToDelete = choice;

        setChoices(currentChoices => {
            return currentChoices.filter(choices => choices !== itemToDelete);
        });
    }

    const onDeleteBallotItemPressedHandler = () => {
        console.log('delete ballot item');
    }

    const onSavePressed = () => {
        if (!ballotItem.ballotItemId) {
            ballot.ballotItems.push({
                ballotItemTitle: title,
                ballotItemDescription: description,
                choices: choices,
            })
        } else {
            ballotItem.ballotItemTitle = title
            ballotItem.ballotItemDescription = description
            ballotItem.choices = choices
        }

        setBallot({ ...ballot })
        setShowBallotItemScreen(false)
    }

    return (
        <KeyboardAvoidingView
            enabled
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={styles.container}
        // keyboardDismissMode="on-drag"
        // keyboardShouldPersistTaps="handled"
        // contentInsetAdjustmentBehavior="always"
        >
            <ScrollView>
                <View style={styles.contentContainer}>
                    {ballot.status && !ballot.canBallotBeModified ? (
                        <InfoMessageBox style={{ marginBottom: 30 }} messageText={`This ballot is ${ballot.status.toLowerCase()} and cannot be modified.`} />
                    ) : (null)}
                    <BallotItemForm
                        titleValue={title}
                        onChangeTitleText={title => setTitle(title)}
                        titleEditable={areTextInputsEditable}
                        descriptionValue={description}
                        onChangeDescriptionText={description => setDescription(description)}
                        descriptionEditable={areTextInputsEditable}
                        choices={choices}
                        onDeleteChoicePressed={deleteChoicePressedHandler}
                        choiceInputControlVisible={areTextInputsEditable}
                        choiceTitleValue={enteredChoiceTitle}
                        onChangeChoiceTitle={enteredChoiceTitle => setEnteredChoiceTitle(enteredChoiceTitle)}
                        choiceExplanationValue={enteredChoiceExplanation}
                        onChangeChoiceExplanation={enteredChoiceExplanation =>
                            setEnteredChoiceExplanation(enteredChoiceExplanation)}
                        onAddChoicePressed={addChoiceHandler}
                        choiceDeleteButtonDisabled={choiceDeleteButtonDisabled}
                        formButtonText='Save Changes'
                        formButtonAccessibilityLabel="Save Changes"
                        formButtonAccessibilityHint="Press this button to save changes for this ballot item"
                        formButtonDisabled={saveButtonDisabled}
                        formButtonPressed={onSavePressed}
                    />

                    {ballot.ballotItemId ? (
                        <LinkStyleButton
                            style={saveButtonDisabled ? styles.disabledDeleteButton : styles.deleteButton}
                            disabled={saveButtonDisabled}
                            onPress={onDeleteBallotItemPressedHandler}
                            buttonText="Delete Ballot Item"
                            accessibilityLabel="Delete Ballot Item"
                            accessibilityHint="Press this button if you wish to delete this entire ballot item"
                        />
                    ) : (null)}

                    {ballot.status && !ballot.canBallotBeModified ? (
                        <InfoMessageBox style={{ marginTop: 30 }} messageText={`This ballot is ${ballot.status.toLowerCase()} and cannot be modified.`} />
                    ) : (null)}
                </View>
            </ScrollView>

        </KeyboardAvoidingView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1
    },
    contentContainer: {
        paddingVertical: 30,
        paddingHorizontal: 20
    },
    disabledDeleteButton: {
        marginTop: 20,
        color: MainColors.lightGray,
        fontWeight: '500',
        fontSize: FontSizes.medium
    },
    deleteButton: {
        marginTop: 20,
        color: MainColors.danger,
        fontWeight: '500',
        fontSize: FontSizes.medium
    },
});

export default CreateOrEditBallotItem;